import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { combineLatest, forkJoin, from, Observable, of, Subject } from 'rxjs';
import {
    concatMap,
    delay,
    finalize,
    map,
    mergeMap,
    shareReplay,
    take,
    tap
} from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { DbKeyService } from '../core/security/db-key.service';
import {
    ThumediDatabase,
    ThumediDbEvent
} from '../core/abstractions/thumedi-database';
import { ThumediDatabaseHelper } from '../core/abstractions/thumedi-database.helper';
import { ThumediRepository } from '../core/abstractions/thumedi.repository';
import { TableStateModel } from '../models/table-state.model';
import { UnsyncedChangeModel } from '../models/unsynced-change.model';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { BackgroundDataLoadService } from './background-data-load.service';
import { OnlineStatusService } from './online-status.service';
import { TableStateResource } from './resources/table-state.resource';
import { SyncStorage } from './storages/sync.storage';
import { buildUnsyncedChangesExport } from './storages/unsynced-changes-export';
import { catchError, takeUntil } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class SyncService {
    private swUpdate = inject(SwUpdate);
    private database = inject(ThumediDatabase);
    private dbHelper = inject(ThumediDatabaseHelper);
    private onlineStatusService = inject(OnlineStatusService);
    private syncStorage = inject(SyncStorage);
    private authService = inject(AuthService);
    private alertService = inject(AlertService);
    private tableStateResource = inject(TableStateResource);
    private router = inject(Router);
    private dbKeyService = inject(DbKeyService);
    private backgroundDataLoadService = inject(BackgroundDataLoadService);
    private logger = inject(LoggerService);

    private readonly _isAppLocked = signal(false);
    /** True once the app is locked and requires a fresh sync to be usable. */
    readonly isAppLocked: Signal<boolean> = this._isAppLocked.asReadonly();

    // Progress is an event sequence, not a single latest value: a bare
    // signal<string> would coalesce identical/rapid next() calls and lose events.
    // We therefore keep the whole log as array state (newest first); each event
    // is an immutable update so nothing is dropped.
    private readonly _progressLog = signal<readonly string[]>([]);
    /** Progress messages for the running sync, newest first. */
    readonly progressLog: Signal<readonly string[]> =
        this._progressLog.asReadonly();
    /** Derived progress bar percentage (0-100+, clamped by the bar's [max]). */
    readonly progressPercent = computed(() =>
        this.computePercent(this._progressLog().length)
    );

    // A completed sync is a one-shot event that drives an RxJS pipeline in
    // DataSyncComponent (mergeMap -> HTTP -> navigation), so it stays a Subject.
    private readonly _hasSyncFinished = new Subject<boolean>();
    readonly hasSyncFinished$ = this._hasSyncFinished.asObservable();

    private readonly _isSyncing = signal(false);
    /** True while a sync run is in flight. */
    readonly isSyncing: Signal<boolean> = this._isSyncing.asReadonly();

    private _serverUpdates: Observable<any>[] = [];
    private _clientUpdates: Observable<any>[] = [];
    private _cancelSync = new Subject<void>();
    /** Guards against stacking multiple DB-failure prompts on top of each other. */
    private _dbErrorPromptOpen = false;

    start(): void {
        this.subscribeToDbEvents();
        this.setupDatabase();
    }

    /**
     * Surfaces critical IndexedDB lifecycle events (Issue #2) to the user with a
     * reload affordance instead of failing silently. Subscribed before the DB is
     * opened so an `openFailed` emitted during `setup()` is not missed. The
     * subscription is intentionally unmanaged: this is a root singleton that
     * lives for the whole app session.
     */
    private subscribeToDbEvents(): void {
        this.database.dbEvents$.subscribe(event => this.handleDbEvent(event));
    }

    private handleDbEvent(event: ThumediDbEvent): void {
        // Never wipe local data here — a DB error must not cost the user their
        // offline edits. We only inform and offer a reload.
        if (this._dbErrorPromptOpen) {
            return;
        }
        this._dbErrorPromptOpen = true;

        const message =
            event.type === 'versionchange'
                ? 'Die App wurde in einem anderen Tab oder Fenster aktualisiert. Bitte laden Sie die App neu.'
                : 'Offline-Datenbank konnte nicht aktualisiert werden. Bitte laden Sie die App neu. Schließen Sie ggf. weitere Tabs dieser App.';

        from(
            this.alertService.showCustomConfirm(
                message,
                'Fehler',
                'Neu laden',
                'Abbrechen'
            )
        )
            .pipe(take(1))
            .subscribe(res => {
                if (res && res.isConfirmed) {
                    window.location.reload();
                } else {
                    // Let a subsequent event re-prompt if the user dismissed it.
                    this._dbErrorPromptOpen = false;
                }
            });
    }

    private setupDatabase(): void {
        // Encryption off (default): open the DB exactly as before.
        if (!environment.encryptIndexedDb) {
            this.database.setup().catch(error => {
                // The user-facing prompt is driven by the `openFailed` event on
                // `dbEvents$` (see subscribeToDbEvents); this catch only prevents
                // an unhandled rejection and keeps a diagnostic breadcrumb.
                this.logger.error('Database setup failed', error);
            });

            return;
        }

        // Encryption on (Issue #4): the DB can only be opened once an in-memory
        // key is available. On a fresh login the key is set during login(); on a
        // page/SW reload it is re-fetched here from the backend.
        this.authService
            .ensureDbKey()
            .pipe(take(1))
            .subscribe(hasKey => {
                if (!hasKey) {
                    // Offline reload with no key: keep the encrypted DB closed
                    // and require an online sign-in to decrypt local data.
                    this._isAppLocked.set(true);
                    this.authService.logout();

                    return;
                }

                this.database
                    .setup(this.dbKeyService.getKey())
                    .catch(error => {
                        this.logger.error('Database setup failed', error);
                    });
            });
    }

    validateSyncRequirement(): void {
        if (this._isSyncing()) {
            return;
        }

        const lastSync = this.syncStorage.getLastSync();

        if (!lastSync) {
            this.authService.logout();
            return;
        }

        const now = new Date();
        const dateDiff = Math.abs(now.getTime() - lastSync.getTime());
        const daysWithoutUpdate = Math.ceil(dateDiff / (1000 * 3600 * 24));
        const warningThreshold =
            environment.notSyncedMaxDays -
            environment.notSyncedPreMaxDaysWarning;

        if (
            daysWithoutUpdate >= warningThreshold &&
            daysWithoutUpdate < environment.notSyncedMaxDays
        ) {
            // Warning phase: we tell the user if no update is done soon, he'll lose access.
            const remainingSyncDays =
                environment.notSyncedMaxDays - daysWithoutUpdate;
            this.alertService.showAlert(
                `In spätestens ${remainingSyncDays} Tagen ist ein Datenabgleich notwendig.`
            );
        } else if (daysWithoutUpdate >= environment.notSyncedMaxDays) {
            // Block phase: we prevent the user from accessing the app until syncing.
            this._isAppLocked.set(true);

            this.alertService.showError(
                `Kein Datenabgleich seit ${daysWithoutUpdate} Tagen.
                Sie können die App danch erst nutzen, wenn Sie erneut synchronisieren.`
            );
            this.authService.logout();
        }
    }

    sync(): void {
        if (this.onlineStatusService.isOnline()) {
            this.performSync();
        }
    }

    resetSync(): void {
        from(
            this.alertService.showCustomConfirm(
                'Do you want to reset sync process?',
                'Warning!',
                'Wiederholen',
                'Abbrechen'
            )
        )
            .pipe(
                take(1),
                mergeMap(res => {
                    if (res && res.isConfirmed) {
                        // Escape hatch (Issue #2): resetSync wipes
                        // `unsyncedChanges`, so export them to a JSON file first
                        // as a last-resort recovery net before the destructive
                        // clear. The export is best-effort and never blocks the
                        // reset.
                        return this.exportUnsyncedChanges().pipe(
                            mergeMap(() => this.clearDatabase()),
                            tap(() => {
                                this._cancelSync.next();
                                window.location.reload();
                            })
                        );
                    }

                    return of(null);
                })
            )
            .subscribe();
    }

    /**
     * Reads the local `unsyncedChanges` and, if any exist, triggers a JSON file
     * download of a versioned export envelope. Best-effort: any failure is
     * logged and swallowed so it can never block a recovery flow. Emits once and
     * completes.
     */
    private exportUnsyncedChanges(): Observable<void> {
        return this.dbHelper.getAll('unsyncedChanges').pipe(
            take(1),
            tap(changes => {
                const list = (changes ?? []) as UnsyncedChangeModel[];
                if (list.length === 0) {
                    return;
                }

                try {
                    this.downloadJson(
                        buildUnsyncedChangesExport(
                            list,
                            this.database.verno
                        ),
                        `thumedi-unsynced-changes-${new Date()
                            .toISOString()
                            .slice(0, 10)}.json`
                    );
                } catch (error) {
                    this.logger.error(
                        'Failed to export unsynced changes',
                        error
                    );
                }
            }),
            map(() => undefined),
            catchError(error => {
                this.logger.error('Failed to read unsynced changes for export', error);

                return of(undefined);
            })
        );
    }

    private downloadJson(payload: unknown, filename: string): void {
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
            type: 'application/json'
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    }

    checkFullSyncNeeded() {
        if (this.onlineStatusService.isOnline()) {
            return this.checkFullSyncRequired();
        }

        return of(null);
    }

    private performSync(): void {
        if (this._isSyncing()) {
            return;
        }

        this._isSyncing.set(true);
        this._progressLog.set([]);

        this.pushProgress('App wird geladen');

        if (environment.production) {
            this.swUpdate.checkForUpdate();
        }

        this.pushProgress('Validierung von Synchronisationsanforderungen');

        this.validateSync()
            .pipe(
                takeUntil(this._cancelSync),
                take(1),
                mergeMap(() => {
                    if (
                        this._serverUpdates.length > 0 &&
                        this._clientUpdates.length > 0
                    ) {
                        return forkJoin(this._serverUpdates).pipe(
                            concatMap(() => forkJoin(this._clientUpdates)),
                            shareReplay()
                        );
                    } else if (this._serverUpdates.length > 0) {
                        return forkJoin(this._serverUpdates).pipe(
                            shareReplay()
                        );
                    } else if (this._clientUpdates.length > 0) {
                        return forkJoin(this._clientUpdates).pipe(
                            shareReplay()
                        );
                    } else {
                        return of(null);
                    }
                }),
                shareReplay(),
                catchError(_err => {
                    return this.errorSync();
                }),
                finalize(() => this.finalizeSync())
            )
            .subscribe();

        this.pushProgress('Synchronisiere...');
    }

    /** Appends a progress message to the log (newest first) without coalescing. */
    private pushProgress(message: string): void {
        this._progressLog.update(log => [message, ...log]);
    }

    /**
     * Reproduces exactly the percentage the DataSyncComponent used to compute
     * from the progressChanged event count: +5 for each of the first two events,
     * then +85/12 for every subsequent event. The bar clamps to [max]=100.
     */
    private computePercent(count: number): number {
        if (count <= 2) {
            return count * 5;
        }

        return 10 + (count - 2) * (85 / 12);
    }

    private validateSync() {
        return combineLatest([
            this.tableStateResource.getCurrent(),
            this.dbHelper.getAll('unsyncedChanges'),
            this.dbHelper.getAll('tableStates')
        ]).pipe(
            mergeMap(res => {
                const tableStates = res[0];
                const unsyncedChanges = res[1] as UnsyncedChangeModel[];
                const tableStatesLocal = res[2] as TableStateModel[];

                const count: Observable<{
                    tableName: any;
                    count: number;
                }>[] = [];

                for (const serverState of tableStates) {
                    count.push(this.hasLocalRecords(serverState.id));
                }
                return forkJoin(count).pipe(
                    map(r => {
                        r.forEach(x => {
                            const unsyncedChangesByTable =
                                unsyncedChanges.filter(
                                    un => un.tableName == x.tableName
                                );

                            const serverState = tableStates.find(
                                st => st.id == x.tableName
                            );

                            const localState = tableStatesLocal.find(
                                ls => ls.id === x.tableName
                            );

                            // If the local count is 0, we need to sync from server.
                            if (
                                !x.count ||
                                x.count === 0 ||
                                (localState?.lastLocalUpdate &&
                                    new Date(serverState.modified) >
                                        new Date(localState.lastLocalUpdate))
                            ) {
                                const isFirstLoad =
                                    !localState?.lastLocalUpdate;

                                this._serverUpdates.push(
                                    this.dbHelper
                                        .getBackendResourceByTable(x.tableName)
                                        .syncFromServer(
                                            localState?.lastLocalUpdate
                                                ? new Date(
                                                      localState.lastLocalUpdate
                                                  )
                                                : undefined,
                                            isFirstLoad
                                        )
                                        .pipe(
                                            concatMap(res => {
                                                this.pushProgress(
                                                    `Lade ${x.tableName}`
                                                );

                                                return this.dbHelper.editRange(
                                                    x.tableName,
                                                    res.records,
                                                    true
                                                );
                                            }),
                                            tap(() => {
                                                this.pushProgress(
                                                    `Synchronisiere ${x.tableName}`
                                                );
                                            }),
                                            catchError(_err => {
                                                return this.errorSync();
                                            })
                                        )
                                );
                            }

                            if (unsyncedChangesByTable.length > 0) {
                                this._clientUpdates.push(
                                    this.dbHelper
                                        .getBackendResourceByTable(x.tableName)
                                        .syncToServerNew(unsyncedChangesByTable)
                                        .pipe(
                                            concatMap(res => {
                                                this.pushProgress(
                                                    `Speichere ${x.tableName}`
                                                );

                                                if (res) {
                                                    return this.dbHelper.deleteRange(
                                                        'unsyncedChanges',
                                                        unsyncedChangesByTable
                                                    );
                                                }

                                                return of(null);
                                            }),
                                            catchError(_err => {
                                                return this.errorSync();
                                            })
                                        )
                                );
                            }
                        });
                    }),
                    concatMap(() =>
                        this.dbHelper.editRange(
                            'tableStates',
                            tableStates.map(x => {
                                x.lastLocalUpdate = x.modified;

                                return x;
                            })
                        )
                    )
                );
            })
        );
    }

    private hasLocalRecords<T extends keyof ThumediRepository>(tableName: T) {
        return this.dbHelper.count(tableName).pipe(
            map(r => {
                return { tableName, count: r };
            })
        );
    }

    private finalizeSync(): void {
        this.pushProgress('Abschluss der Synchronisierung');

        if (!this.syncStorage.lastFullSyncExists()) {
            this.syncStorage.saveLastFullSync(new Date());

            // We should only perfom this operation if is the first sync
            this.startBackgroundBookingsSync();
        }

        this.syncStorage.saveLastSync(new Date());

        this._hasSyncFinished.next(true);

        this._serverUpdates = [];
        this._clientUpdates = [];
        this._isAppLocked.set(false);
        this._isSyncing.set(false);
    }

    private checkFullSyncRequired() {
        if (this.syncStorage.lastFullSyncExists()) {
            return this.tableStateResource
                .validateFullSync(this.syncStorage.getLastFullSync())
                .pipe(
                    mergeMap(shouldFullSync => {
                        if (shouldFullSync) {
                            return this.prepareFullSync();
                        }

                        return of(null);
                    })
                );
        }

        return of(null);
    }

    private prepareFullSync() {
        return from(
            this.alertService.showCustomConfirm(
                'Es kann sein, dass Sie ihre ungespeicherten Daten verlieren',
                'Vollständige Synchronisierung erforderlich',
                'Ok'
            )
        ).pipe(
            concatMap(() => {
                this.syncStorage.eraseLastFullSync();

                return this.clearDatabase().pipe(
                    concatMap(() => from(this.router.navigate(['data-sync'])))
                );
            })
        );
    }

    private errorSync() {
        return from(
            this.alertService.showCustomConfirm(
                'Bei der Synchronisierung ist ein Fehler aufgetreten. Möchten Sie es erneut versuchen?',
                'Ein Fehler trat auf',
                'Wiederholen',
                'Abbrechen'
            )
        ).pipe(
            mergeMap(res => {
                if (res && res.isConfirmed) {
                    // Preserve pending local changes across an error retry: the
                    // whole point of the retry is to re-download server data and
                    // re-upload the user's offline edits, so wiping
                    // `unsyncedChanges` here would guarantee their loss. The
                    // destructive full-sync path (prepareFullSync) still clears
                    // everything, but only after an explicit user warning.
                    return this.clearDatabase(true).pipe(
                        finalize(() => this.sync())
                    );
                }

                return of(null);
            })
        );
    }

    private clearDatabase(preserveUnsyncedChanges = false) {
        const clears = [
            from(this.dbHelper.getTable('bookings').clear()),
            from(this.dbHelper.getTable('customers').clear()),
            from(this.dbHelper.getTable('allocations').clear()),
            from(this.dbHelper.getTable('providers').clear()),
            from(this.dbHelper.getTable('todos').clear()),
            from(this.dbHelper.getTable('templates').clear()),
            from(this.dbHelper.getTable('tableStates').clear())
        ];

        if (!preserveUnsyncedChanges) {
            clears.push(from(this.dbHelper.getTable('unsyncedChanges').clear()));
        }

        return forkJoin(clears);
    }

    private startBackgroundBookingsSync() {
        // Delegate to the main-thread proxy: it fetches via HttpClient (so the
        // auth/error interceptors run) and hands only data to the workers — the
        // token never leaves the main thread (Issue #5). The 1s delay preserves
        // the previous behaviour of letting the foreground sync settle first.
        of({})
            .pipe(
                take(1),
                delay(1000),
                tap(() => this.backgroundDataLoadService.start())
            )
            .subscribe();
    }
}
